import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Badge, Button } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { NormalizedPendingItem } from "../hooks/usePendingActions";
import { PENDING_CATEGORY_MAP, getItemCategory } from "../consts";
import { isTaskDeadlineDetails } from "../types/pending.types";

interface Props {
  item: NormalizedPendingItem;
  onClick: () => void;
}

function getLocalizedSubject(item: NormalizedPendingItem, language: string): string {
  switch (language) {
    case "kk":
      return item.subject_kk;
    case "en":
      return item.subject_en;
    default:
      return item.subject_ru;
  }
}

const PRIORITY_BADGE_COLOR = {
  urgent: "negative",
  high: "notice",
  normal: "gray",
  low: "gray",
} as const;

function getDeadlineText(
  item: NormalizedPendingItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): { text: string; isOverdue: boolean } | null {
  if (item.originalType !== "task_deadline_approaching" || !isTaskDeadlineDetails(item.details)) return null;
  const { days_remaining, is_overdue } = item.details;
  if (days_remaining === 0 && !is_overdue) return { text: t("deadline.today"), isOverdue: false };
  if (is_overdue) return { text: t("deadline.overdue", { count: Math.abs(days_remaining) }), isOverdue: true };
  return { text: t("deadline.remaining", { count: days_remaining }), isOverdue: false };
}

export default function PendingActionCard({ item, onClick }: Props) {
  const { t, i18n } = useTranslation("PendingActionsPage");
  const category = getItemCategory(item.originalType);
  const categoryConfig = PENDING_CATEGORY_MAP[category];
  const CategoryIcon = categoryConfig.icon;
  const deadline = getDeadlineText(item, t);

  return (
    <div
      className="flex items-center gap-2.5 p-3 radius-lg surface-base-fill cursor-pointer hover:opacity-90 transition-opacity duration-150"
      onClick={onClick}>
      <div className="w-[50px] aspect-square shrink-0 flex justify-center items-center radius-sm surface-component-fill content-base-low">
        <CategoryIcon size={24} color="currentColor" variant="Bold" />
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <p className="text-body-bold-md content-base-primary truncate">{getLocalizedSubject(item, i18n.language)}</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant="filled" color={PRIORITY_BADGE_COLOR[item.priority]} text={t(`priority.${item.priority}`)} />
            <Badge variant="soft" color={categoryConfig.badgeColor} text={t(`categories.${category}`)} />
          </div>
          <div className="flex items-center gap-1.5">
            {!deadline && (
              <span className="text-body-regular-sm content-base-secondary">{formatDateForDisplay(item.createdAt)}</span>
            )}
            {deadline && (
              <span
                className={cn(
                  "text-label-sm",
                  deadline.isOverdue ? "content-action-negative" : "content-action-notice",
                )}>
                {deadline.text}
              </span>
            )}
          </div>
        </div>
      </div>

      <Button variant="tertiary" size="md">
        {t(`cardAction.${item.actionRequired}`)}
      </Button>
    </div>
  );
}
